/**
 * 🔒 Raw Materials — spec docs/superpowers/specs/2026-09-21-raw-materials-LOCKED-SPEC.md
 * Columns: Min, Max, Physical, Reserved, Available (= physical − reserved), In Transit (click → POs),
 * Tags / Used in, Status. Filters: Urgently needed, Below min, Frequent, Vendor, Domestic/Import, Tag, search.
 * Approver roles add, edit (min/max/vendor/sourcing/tags) and approve. Server does the maths and the rules.
 * Used by the Inventory → Raw Material tab and by the /raw-materials page.
 */
import { useCallback, useEffect, useState } from 'react';
import { Boxes, Check, Loader2, Pencil, Plus } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { isMasterdataApprover } from '@/lib/masterdataApprovers';
import { ProductVendorsBlock } from '../procurement/vendorProducts/ProductVendorsBlock';

export const RAW_MATERIAL = 'Raw Material';
const UNITS = ['PCS', 'KGS', 'LTR', 'MTR', 'SQM', 'SET'];
const SUB_CATEGORIES = ['Sheet & Structural', 'Fittings', 'Insulation & Chemicals', 'Packing', 'Labels', 'Hardware'];
const LIMIT = 25;

export function nextRawMaterialSku(skus: Array<string | null | undefined>): string {
  const n = Math.max(0, ...skus.map((s) => Number(/^RM-(\d+)$/.exec(s ?? '')?.[1] ?? 0)));
  return `RM-${String(n + 1).padStart(4, '0')}`;
}
export function parseTags(s: string): string[] {
  return [...new Set(s.split(',').map((t) => t.trim()).filter(Boolean))];
}

type Vendor = { id: string; name: string; country: string | null };
type OpenPo = { poNumber: string; vendorName: string | null; openQty: number; expectedDate: string | null };
type Row = {
  id: string; sku: string | null; name: string; subCategory: string | null; unit: string | null; tags: string[]; usedIn: Array<{ sku: string | null; name: string }>;
  sourcing: 'domestic' | 'import' | null; vendor: Vendor | null; min: number | null; max: number | null; physical: number; reserved: number; available: number;
  inTransit: number; openPos: OpenPo[]; belowMin: boolean; urgent: boolean; frequent: boolean; orders90d: number; suggestedMin: number | null; suggestedMax: number | null; rmApprovalStatus: string | null;
};
type Filters = { urgent: boolean; belowMin: boolean; frequent: boolean; vendorId: string; sourcing: '' | 'domestic' | 'import'; tag: string; search: string };
const NO_FILTERS: Filters = { urgent: false, belowMin: false, frequent: false, vendorId: '', sourcing: '', tag: '', search: '' };
type Form = { name: string; unit: string; subCategory: string; min: string; max: string; vendorId: string; sourcing: '' | 'domestic' | 'import'; tags: string };
const EMPTY_FORM: Form = { name: '', unit: 'PCS', subCategory: SUB_CATEGORIES[0], min: '', max: '', vendorId: '', sourcing: '', tags: '' };

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No date');
const fmt = (n: number | null) => (n === null || n === undefined ? '—' : String(n));

function params(f: Filters, page: number): Record<string, string> {
  const p: Record<string, string> = { page: String(page), limit: String(LIMIT) };
  if (f.urgent) p.urgent = 'true';
  if (f.belowMin) p.belowMin = 'true';
  if (f.frequent) p.frequent = 'true';
  if (f.vendorId) p.vendorId = f.vendorId;
  if (f.sourcing) p.sourcing = f.sourcing;
  if (f.tag.trim()) p.tag = f.tag.trim();
  if (f.search.trim()) p.search = f.search.trim();
  return p;
}

export function RawMaterialsPanel() {
  const { user } = useAuthStore();
  const approver = isMasterdataApprover(user as any);
  const [rows, setRows] = useState<Row[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [nextSku, setNextSku] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPosFor, setOpenPosFor] = useState<string | null>(null);
  const [vendorsFor, setVendorsFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<'new' | Row | null>(null);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await api.get('/products/raw-materials', { params: params(filters, page) });
      setRows(res.data.data?.rows ?? []);
      setVendors(res.data.data?.options?.vendors ?? []);
      setNextSku(res.data.data?.options?.nextSku ?? null);
      setTotal(res.data.pagination?.total ?? 0);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load raw materials');
    } finally { setLoading(false); }
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (patch: Partial<Filters>) => { setPage(1); setFilters((f) => ({ ...f, ...patch })); };
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const startAdd = () => { setForm(EMPTY_FORM); setFormError(null); setEditing('new'); };
  const startEdit = (r: Row) => {
    setForm({ name: r.name, unit: r.unit ?? 'PCS', subCategory: r.subCategory ?? SUB_CATEGORIES[0], min: r.min == null ? '' : String(r.min), max: r.max == null ? '' : String(r.max), vendorId: r.vendor?.id ?? '', sourcing: r.sourcing ?? '', tags: r.tags.join(', ') });
    setFormError(null); setEditing(r);
  };

  const save = async () => {
    const min = form.min.trim() === '' ? 0 : Number(form.min);
    const max = form.max.trim() === '' ? null : Number(form.max);
    if (Number.isNaN(min) || (max !== null && Number.isNaN(max)) || min < 0 || (max !== null && max < 0)) { setFormError('Min and Max must be numbers, 0 or more.'); return; }
    if (max !== null && max > 0 && min > max) { setFormError('Max must be at least Min.'); return; }
    const common = { reorderLevel: min, maxLevel: max, preferredVendorId: form.vendorId || null, sourcing: form.sourcing || null, tags: parseTags(form.tags) };
    setBusy('save'); setFormError(null);
    try {
      if (editing === 'new') {
        const name = form.name.trim().replace(/\s+/g, ' ');
        if (!name) { setFormError('Enter the material name.'); setBusy(null); return; }
        await api.post('/products', {
          name, sku: nextSku ?? nextRawMaterialSku(rows.map((r) => r.sku)), productType: RAW_MATERIAL, category: RAW_MATERIAL,
          subCategory: form.subCategory, unit: form.unit, unitOfMeasure: form.unit, costPrice: 0, unitPrice: 0, costPriceOnly: true, gstRate: 18,
          ...common,
        });
      } else if (editing) {
        await api.patch(`/products/${editing.id}/raw-material`, { ...common, unit: form.unit, subCategory: form.subCategory });
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setFormError(e.response?.data?.error || 'Could not save the material.');
    } finally { setBusy(null); }
  };

  const approve = async (id: string) => {
    setBusy(id);
    try { await api.post(`/products/${id}/approve-material`); await load(); }
    catch (e: any) { setError(e.response?.data?.error || 'Could not approve the material.'); }
    finally { setBusy(null); }
  };

  const input = 'mt-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-900';
  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3">
        <input aria-label="Search raw materials" placeholder="Search name or code" value={filters.search} onChange={(e) => setFilter({ search: e.target.value })} className="min-w-[180px] flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
        {([['urgent', 'Urgently needed'], ['belowMin', 'Below min'], ['frequent', 'Frequent']] as const).map(([k, label]) => (
          <label key={k} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" aria-label={label} checked={filters[k]} onChange={(e) => setFilter({ [k]: e.target.checked } as Partial<Filters>)} /> {label}
          </label>
        ))}
        <select aria-label="Vendor" value={filters.vendorId} onChange={(e) => setFilter({ vendorId: e.target.value })} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
          <option value="">All vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select aria-label="Domestic / Import" value={filters.sourcing} onChange={(e) => setFilter({ sourcing: e.target.value as Filters['sourcing'] })} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
          <option value="">Domestic + Import</option>
          <option value="domestic">Domestic</option>
          <option value="import">Import</option>
        </select>
        <input aria-label="Tag" placeholder="Tag or product" value={filters.tag} onChange={(e) => setFilter({ tag: e.target.value })} className="w-36 rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
        {approver && editing === null && (
          <button type="button" onClick={startAdd} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800">
            <Plus size={14} /> Add raw material
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {editing !== null && (
        <div data-testid="rm-form" className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
          <p className="w-full text-sm font-semibold text-gray-900">{editing === 'new' ? 'Add raw material' : `Edit ${editing.sku ?? ''} ${editing.name}`}</p>
          {editing === 'new' && (
            <label className="flex min-w-[220px] flex-1 flex-col text-xs text-gray-600">Material name
              <input aria-label="Material name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
            </label>
          )}
          <label className="flex flex-col text-xs text-gray-600">Unit
            <select aria-label="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={input}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select>
          </label>
          <label className="flex flex-col text-xs text-gray-600">Sub-category
            <select aria-label="Sub-category" value={form.subCategory} onChange={(e) => setForm({ ...form, subCategory: e.target.value })} className={input}>{SUB_CATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </label>
          <label className="flex w-24 flex-col text-xs text-gray-600">Min
            <input aria-label="Min" inputMode="decimal" value={form.min} onChange={(e) => setForm({ ...form, min: e.target.value })} className={input} />
          </label>
          <label className="flex w-24 flex-col text-xs text-gray-600">Max
            <input aria-label="Max" inputMode="decimal" value={form.max} onChange={(e) => setForm({ ...form, max: e.target.value })} className={input} />
          </label>
          <label className="flex flex-col text-xs text-gray-600">Preferred vendor
            <select aria-label="Preferred vendor" value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} className={input}>
              <option value="">None</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-gray-600">Domestic / Import
            <select aria-label="Domestic / Import" value={form.sourcing} onChange={(e) => setForm({ ...form, sourcing: e.target.value as Form['sourcing'] })} className={input}>
              <option value="">Not set</option>
              <option value="domestic">Domestic</option>
              <option value="import">Import</option>
            </select>
          </label>
          <label className="flex min-w-[180px] flex-1 flex-col text-xs text-gray-600">Tags
            <input aria-label="Tags" placeholder="comma separated" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={input} />
          </label>
          <button type="button" onClick={save} disabled={busy === 'save'} className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Save material</button>
          <button type="button" onClick={() => { setEditing(null); setFormError(null); }} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm">Cancel</button>
          {formError && <p className="w-full text-sm text-red-700">{formError}</p>}
        </div>
      )}

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[var(--ink-0)]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-gray-500">
          <Boxes className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm font-medium">No raw materials yet</p>
          <p className="text-xs">Set a product&apos;s type to &quot;Raw Material&quot; to see it here</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-3 text-left">Code</th>
                <th className="px-3 py-3 text-left">Material</th>
                <th className="px-3 py-3 text-left">Tags / Used in</th>
                <th className="px-3 py-3 text-right">Min</th>
                <th className="px-3 py-3 text-right">Max</th>
                <th className="px-3 py-3 text-right">Physical</th>
                <th className="px-3 py-3 text-right">Reserved</th>
                <th className="px-3 py-3 text-right">Available</th>
                <th className="px-3 py-3 text-right">In Transit</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.flatMap((r) => {
                const pending = r.rmApprovalStatus === 'pending';
                const main = (
                  <tr key={r.id} className="align-top">
                    <td className="px-3 py-3 font-mono text-xs text-gray-600">{r.sku ?? '—'}</td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-500">{[r.unit, r.subCategory, r.vendor?.name, r.sourcing === 'import' ? 'Import' : r.sourcing === 'domestic' ? 'Domestic' : null].filter(Boolean).join(' · ')}</p>
                      {r.frequent && (
                        <p className="mt-1 text-xs"><span className="rounded-full bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700">Frequent</span>
                          {r.suggestedMin !== null && <span className="ml-1.5 text-gray-500">Suggested min {r.suggestedMin} · max {r.suggestedMax}</span>}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      <div className="flex flex-wrap gap-1">{r.tags.map((t) => <span key={t} className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{t}</span>)}</div>
                      {r.usedIn.length > 0 && <p className="mt-1 text-gray-500">Used in: {r.usedIn.map((u) => u.sku ?? u.name).join(', ')}</p>}
                    </td>
                    <td className="px-3 py-3 text-right" data-v={fmt(r.min)}>{fmt(r.min)}</td>
                    <td className="px-3 py-3 text-right" data-v={fmt(r.max)}>{fmt(r.max)}</td>
                    <td className="px-3 py-3 text-right" data-v={String(r.physical)}>{r.physical}</td>
                    <td className="px-3 py-3 text-right" data-v={String(r.reserved)}>{r.reserved}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${r.urgent ? 'text-red-700' : r.belowMin ? 'text-amber-700' : 'text-gray-900'}`} data-v={String(r.available)}>
                      {r.available}
                      {r.urgent ? <span className="block text-[11px] font-normal">Urgently needed</span> : r.belowMin ? <span className="block text-[11px] font-normal">Below min</span> : null}
                    </td>
                    <td className="px-3 py-3 text-right" data-v={String(r.inTransit)}>
                      {r.inTransit > 0
                        ? <button type="button" onClick={() => setOpenPosFor(openPosFor === r.id ? null : r.id)} className="font-medium text-blue-700 underline-offset-2 hover:underline">{r.inTransit}</button>
                        : r.inTransit}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${pending ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-700'}`}>{pending ? 'Pending approval' : 'Approved'}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <button type="button" onClick={() => setVendorsFor(vendorsFor === r.id ? null : r.id)} aria-expanded={vendorsFor === r.id} className="mr-1 inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        Vendors
                      </button>
                      {pending && approver && (
                        <button type="button" onClick={() => approve(r.id)} disabled={busy === r.id} className="mr-1 inline-flex items-center gap-1 rounded-lg border border-green-600 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50">
                          <Check size={12} /> Approve
                        </button>
                      )}
                      {approver && (
                        <button type="button" onClick={() => startEdit(r)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          <Pencil size={12} /> Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
                const vendorsRow = vendorsFor === r.id ? (
                  <tr key={`${r.id}-vendors`} className="bg-gray-50">
                    <td colSpan={11} className="px-3 py-3"><ProductVendorsBlock product={{ id: r.id, name: r.name, sku: r.sku }} onChanged={load} /></td>
                  </tr>
                ) : null;
                if (openPosFor !== r.id) return vendorsRow ? [main, vendorsRow] : [main];
                return [main, ...(vendorsRow ? [vendorsRow] : []), (
                  <tr key={`${r.id}-pos`} className="bg-gray-50">
                    <td colSpan={11} className="px-3 py-2" data-testid={`open-pos-${r.id}`}>
                      <table className="text-xs">
                        <thead><tr className="text-gray-500"><th className="pr-6 text-left">PO</th><th className="pr-6 text-left">Vendor</th><th className="pr-6 text-right">Open qty</th><th className="text-left">Expected</th></tr></thead>
                        <tbody>{r.openPos.map((p) => (
                          <tr key={p.poNumber}><td className="pr-6 font-mono">{p.poNumber}</td><td className="pr-6">{p.vendorName ?? '—'}</td><td className="pr-6 text-right">{p.openQty}</td><td>{fmtDate(p.expectedDate)}</td></tr>
                        ))}</tbody>
                      </table>
                    </td>
                  </tr>
                )];
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Page {page} of {totalPages} · {total} materials</span>
          <span className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded border border-gray-200 px-2 py-0.5 disabled:opacity-40">Previous</button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border border-gray-200 px-2 py-0.5 disabled:opacity-40">Next</button>
          </span>
        </div>
      )}
    </div>
  );
}

/** /raw-materials — the same panel as its own page, for approver roles without the full Inventory screen. */
export function RawMaterialsPage() {
  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Raw Materials</h1>
        <p className="text-sm text-gray-500">Available = physical − reserved for production. Urgently needed = still at or under Min after what is in transit arrives.</p>
      </div>
      <RawMaterialsPanel />
    </div>
  );
}
